import { Building2, Plus, SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { problemMessage } from '../api/problem';
import { StateMessage } from '../ui/StateMessage';
import { OrganizationItem } from './OrganizationItem';
import type { Acts } from './use-organization-acts';
import type { OrganizationRow, useOrganizations } from './use-organizations';

type ListState = ReturnType<typeof useOrganizations>['state'];

function EmptyState({
  filtered,
  emptyTenant,
  canCreate,
  tenantName,
  onClear,
  onCreate,
}: {
  readonly filtered: boolean;
  readonly emptyTenant: boolean;
  readonly canCreate: boolean;
  readonly tenantName: string;
  readonly onClear: () => void;
  readonly onCreate: () => void;
}) {
  const { t } = useTranslation('administration');
  if (filtered && !emptyTenant) {
    return (
      <StateMessage
        icon={SearchX}
        title={t('organizations.noResults')}
        action={
          <Button variant="outline" onClick={onClear}>
            {t('organizations.clear')}
          </Button>
        }
      />
    );
  }
  return (
    <StateMessage
      icon={Building2}
      title={t('organizations.firstUse.title', { tenant: tenantName })}
      text={t('organizations.firstUse.text')}
      action={
        canCreate ? (
          <Button onClick={onCreate}>
            <Plus />
            {t('organizations.new')}
          </Button>
        ) : undefined
      }
    />
  );
}

function ListFoot({
  state,
  more,
  reload,
}: {
  readonly state: ListState;
  readonly more: () => void;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  const totals = state.status === 'ready' ? state.page.totals : null;
  return (
    <div className="people-list-foot">
      <span>
        {totals === null
          ? null
          : `${t('organizations.totals', { count: totals.active + totals.archived })}${
              totals.archived > 0
                ? t('organizations.totalsArchived', { count: totals.archived })
                : ''
            }`}
      </span>
      {state.status === 'ready' && state.hasMore ? (
        <Button variant="outline" size="sm" onClick={more} disabled={state.loadingMore}>
          {state.loadingMore ? t('common.loading') : t('common.more')}
        </Button>
      ) : null}
      {state.status === 'error' ? (
        <span role="alert" className="flex items-center gap-2">
          {problemMessage(t, state.code, state.httpStatus)}
          <Button variant="outline" size="sm" onClick={reload}>
            {t('common.retry')}
          </Button>
        </span>
      ) : null}
    </div>
  );
}

function ListRows({
  state,
  tenantName,
  acts,
  onRename,
  cardHrefFor,
}: {
  readonly state: ListState;
  readonly tenantName: string;
  readonly acts: Acts;
  readonly onRename: (row: OrganizationRow) => void;
  readonly cardHrefFor: (row: OrganizationRow) => string;
}) {
  const { t } = useTranslation('administration');
  const totals = state.status === 'ready' ? state.page.totals : null;
  return (
    <div
      className="people-list place-list organizations-list"
      role="list"
      aria-label={t('organizations.listLabel', { tenant: tenantName })}
    >
      <div className="people-list-head" aria-hidden="true">
        <span>{t('organizations.columns.organization')}</span>
        <span>{t('organizations.columns.status')}</span>
        <span>{t('organizations.columns.people')}</span>
        <span />
      </div>
      {state.items.map((row) => (
        <OrganizationItem
          key={row.id}
          row={row}
          lastActive={totals !== null && totals.active === 1}
          acts={acts}
          onRename={() => onRename(row)}
          cardHref={cardHrefFor(row)}
        />
      ))}
    </div>
  );
}

/** The list of organizations, or the loading, error or empty state that stands in for it. */
export function OrganizationListPane({
  state,
  more,
  reload,
  filtered,
  emptyTenant,
  canCreate,
  tenantName,
  acts,
  onClear,
  onCreate,
  onRename,
  cardHrefFor,
}: {
  readonly state: ListState;
  readonly more: () => void;
  readonly reload: () => void;
  readonly filtered: boolean;
  readonly emptyTenant: boolean;
  readonly canCreate: boolean;
  readonly tenantName: string;
  readonly acts: Acts;
  readonly onClear: () => void;
  readonly onCreate: () => void;
  readonly onRename: (row: OrganizationRow) => void;
  readonly cardHrefFor: (row: OrganizationRow) => string;
}) {
  const { t } = useTranslation('administration');
  if (state.status === 'loading' && state.items.length === 0) {
    return (
      <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
        {[0, 1, 2].map((n) => (
          <Skeleton key={n} className="h-16 w-full" />
        ))}
      </SkeletonRegion>
    );
  }
  if (state.status === 'error' && state.items.length === 0) {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={Building2}
        title={t('organizations.loadError')}
        text={problemMessage(t, state.code, state.httpStatus)}
        action={
          <Button variant="outline" onClick={reload}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  }
  if (state.items.length === 0) {
    return (
      <EmptyState
        filtered={filtered}
        emptyTenant={emptyTenant}
        canCreate={canCreate}
        tenantName={tenantName}
        onClear={onClear}
        onCreate={onCreate}
      />
    );
  }
  return (
    <>
      <ListRows
        state={state}
        tenantName={tenantName}
        acts={acts}
        onRename={onRename}
        cardHrefFor={cardHrefFor}
      />
      <ListFoot state={state} more={more} reload={reload} />
    </>
  );
}
